import { DragDropRoot, DragOverlay, restoreDragFocus } from "@z/dnd";
import { paneDrop } from "./dnd";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { LayoutTree, Pane } from "../model/types";
import { cn } from "./cn";
import { LayoutContext } from "./LayoutContext";
import { NodeView } from "./nodes";
import { allGroups, allPanes } from "../model/tree";
import { activateTab } from "../model/ops";
import { paneTitle } from "./PaneRegistry";
import type { PaneRegistry } from "./PaneRegistry";
import { useLayoutKeyboard } from "./useLayoutKeyboard";
import { WorkspaceDockTargets } from "./WorkspaceDockTargets";

export type LayoutRootProps = {
  tree: LayoutTree;
  /** Called with a new tree for every user action; the caller owns and persists the tree. */
  onChange: (tree: LayoutTree) => void;
  registry: PaneRegistry;
  className?: string;
  /** App-provided starting point when the last pane is closed. */
  emptyState?: ReactNode;
  /** Attach the keyboard shortcuts (default true). See `useLayoutKeyboard`. */
  keyboard?: boolean;
  /** Double-click on a tab, e.g. to pin a preview tab. */
  onTabDoubleClick?: (pane: Pane) => void;
};

export function LayoutRoot({
  tree,
  onChange,
  registry,
  className,
  emptyState,
  keyboard = true,
  onTabDoubleClick,
}: LayoutRootProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = (rect: { width: number; height: number }) => {
      if (rect.width > 0) setCompact(rect.width < 900);
      setSize((previous) =>
        previous?.width === rect.width && previous?.height === rect.height
          ? previous
          : { width: rect.width, height: rect.height },
      );
    };
    measure(root.getBoundingClientRect());
    const observer = new ResizeObserver(([entry]) => {
      if (entry) measure(entry.contentRect);
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  const [maximized, setMaximized] = useState(false);
  const [draggingPaneId, setDraggingPaneId] = useState<string | null>(null);
  useLayoutKeyboard(rootRef, tree, onChange, keyboard);

  return (
    <LayoutContext
      value={{
        tree,
        onChange,
        registry,
        draggingPaneId,
        setDraggingPaneId,
        onTabDoubleClick,
        maximized,
        compact,
        setMaximized,
      }}
    >
      <DragDropRoot
        onDragStart={({ operation }) => setDraggingPaneId(String(operation.source?.id))}
        onDragEnd={(event) => {
          setDraggingPaneId(null);
          const next = paneDrop(tree, event);
          if (next !== tree) onChange(next);
          if (event.nativeEvent instanceof KeyboardEvent && event.operation.source) {
            restoreDragFocus(rootRef.current, String(event.operation.source.id));
          }
        }}
      >
        <div
          ref={rootRef}
          data-layout-root=""
          data-dragging={draggingPaneId ?? undefined}
          className={cn(
            "relative flex flex-col h-full min-h-0 w-full min-w-0 overflow-hidden bg-bg text-fg",
            className,
          )}
        >
          {compact && allGroups(tree).length > 1 && (
            <label className="flex h-11 shrink-0 items-center gap-3 border-b border-border bg-surface px-3 text-xs text-fg-muted">
              Open views
              <select
                aria-label="Open views"
                value={tree.focusedPaneId ?? ""}
                onChange={(event) => onChange(activateTab(tree, event.target.value))}
                className="h-9 min-w-0 flex-1 rounded-md border border-border bg-bg px-2 text-sm text-fg field-focus"
              >
                {allPanes(tree).map((pane) => (
                  <option key={pane.id} value={pane.id}>
                    {paneTitle(registry, pane)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="relative min-h-0 min-w-0 flex-1">
            {allPanes(tree).length === 0 && emptyState !== undefined
              ? emptyState
              : size !== null && (
                  <NodeView
                    width={size.width}
                    height={Math.max(
                      0,
                      size.height - (compact && allGroups(tree).length > 1 ? 44 : 0),
                    )}
                  />
                )}
            <WorkspaceDockTargets />
          </div>
        </div>
        <DragOverlay dropAnimation={null}>
          {(source) => (
            <div className="rounded-md border border-accent/40 bg-bg px-3 py-2 text-sm text-fg shadow-lg">
              {String(source.data["label"] ?? "Tab")}
            </div>
          )}
        </DragOverlay>
      </DragDropRoot>
    </LayoutContext>
  );
}
